import type {
  AccountBrand,
  AccountDashboardDefaults,
  AccountDeliverySettings,
  AccountGoals,
  AccountProfile,
  GuestDataSettings,
  ResponseSettings,
  ReviewWidgetConfig,
} from '@common/types';

import { PROPERTIES } from './group-scope.seed';
import { DEFAULT_DASHBOARD_LAYOUT } from './user-preferences.data';

const CITY_META: Readonly<
  Record<string, { country: string; lat: number; lng: number; tz: string }>
> = {
  Johannesburg: { country: 'South Africa', lat: -26.1076, lng: 28.0567, tz: 'Africa/Johannesburg' },
  'Cape Town': { country: 'South Africa', lat: -33.9036, lng: 18.4201, tz: 'Africa/Johannesburg' },
  Gqeberha: { country: 'South Africa', lat: -33.9833, lng: 25.6667, tz: 'Africa/Johannesburg' },
  Rustenburg: { country: 'South Africa', lat: -25.3411, lng: 27.0919, tz: 'Africa/Johannesburg' },
  'Port Edward': {
    country: 'South Africa',
    lat: -31.0508,
    lng: 30.2244,
    tz: 'Africa/Johannesburg',
  },
  Livingstone: { country: 'Zambia', lat: -17.8419, lng: 25.8543, tz: 'Africa/Lusaka' },
  Windhoek: { country: 'Namibia', lat: -22.5597, lng: 17.0832, tz: 'Africa/Windhoek' },
  Lagos: { country: 'Nigeria', lat: 6.4281, lng: 3.4219, tz: 'Africa/Lagos' },
  London: { country: 'United Kingdom', lat: 51.5072, lng: -0.1276, tz: 'Europe/London' },
  Paris: { country: 'France', lat: 48.8566, lng: 2.3522, tz: 'Europe/Paris' },
  Madrid: { country: 'Spain', lat: 40.4168, lng: -3.7038, tz: 'Europe/Madrid' },
  Amsterdam: { country: 'Netherlands', lat: 52.3676, lng: 4.9041, tz: 'Europe/Amsterdam' },
  Como: { country: 'Italy', lat: 45.8081, lng: 9.0852, tz: 'Europe/Rome' },
  Dubai: { country: 'United Arab Emirates', lat: 25.2048, lng: 55.2708, tz: 'Asia/Dubai' },
  Doha: { country: 'Qatar', lat: 25.2854, lng: 51.531, tz: 'Asia/Qatar' },
  Jeddah: { country: 'Saudi Arabia', lat: 21.4858, lng: 39.1925, tz: 'Asia/Riyadh' },
  'Abu Dhabi': { country: 'United Arab Emirates', lat: 24.4539, lng: 54.3773, tz: 'Asia/Dubai' },
  Singapore: { country: 'Singapore', lat: 1.3521, lng: 103.8198, tz: 'Asia/Singapore' },
  'Hong Kong': { country: 'China', lat: 22.3193, lng: 114.1694, tz: 'Asia/Hong_Kong' },
  Tokyo: { country: 'Japan', lat: 35.6762, lng: 139.6503, tz: 'Asia/Tokyo' },
};

const KINDS = ['hotel', 'resort', 'lodge', 'hotel', 'resort'] as const;
const STREETS = ['Marine Parade', 'Beach Road', 'Palm Avenue', 'Harbour Way', 'Station Street'];

function buildProfiles(): Record<string, AccountProfile> {
  const map: Record<string, AccountProfile> = {};
  PROPERTIES.forEach((property, index) => {
    const meta = CITY_META[property.location] ?? {
      country: property.region,
      lat: 0,
      lng: 0,
      tz: 'UTC',
    };
    map[property.id] = {
      accountId: property.id,
      displayName: property.name,
      legalEntity: `${property.name} (Pty) Ltd`,
      kind: KINDS[index % KINDS.length],
      starRating: 3 + (index % 3),
      roomCount: 84 + ((index * 37) % 340),
      address: {
        line1: `${12 + index * 7} ${STREETS[index % STREETS.length]}`,
        line2: '',
        city: property.location,
        region: property.region,
        postalCode: `${1000 + index * 111}`,
        country: meta.country,
      },
      latitude: meta.lat,
      longitude: meta.lng,
      contact: {
        name: 'Reception',
        email: `reception@${property.id}.suninternational.com`,
        phone: '+27 11 780 7000',
      },
      websiteUrl: `https://www.suninternational.com/${property.id}`,
      bookingUrl: `https://book.suninternational.com/${property.id}`,
      checkInTime: '14:00',
      checkOutTime: '11:00',
      description:
        'A flagship property in the group, with conferencing, spa and a full food and beverage offering.',
    };
  });
  return map;
}

export const ACCOUNT_PROFILES_SEED: Readonly<Record<string, AccountProfile>> = buildProfiles();

/**
 * These hex values are guest-facing brand *content*, not app styling, so §2.3's token rule does not
 * apply: a customer picks them and we render them through inline custom properties on a scoped
 * element. They must never reach a stylesheet.
 */
export const ACCOUNT_BRAND_DEFAULT: Omit<AccountBrand, 'accountId'> = {
  logoUrl: '',
  primaryColor: '#00a0dc',
  secondaryColor: '#50b4c8',
  emailHeaderUrl: '',
  emailFooterUrl: '',
  buttonColor: '#00a0dc',
  textColor: '#1c262f',
  linkColor: '#036595',
};

export const ACCOUNT_DELIVERY_DEFAULT: Omit<AccountDeliverySettings, 'accountId'> = {
  rules: [
    {
      questionnaireType: 'post-stay',
      trigger: 'after-checkout',
      offsetDays: 1,
      reminderCount: 2,
      reminderSpacingDays: 3,
      isEnabled: true,
    },
    {
      questionnaireType: 'pre-stay',
      trigger: 'before-arrival',
      offsetDays: 3,
      reminderCount: 0,
      reminderSpacingDays: 0,
      isEnabled: true,
    },
    {
      questionnaireType: 'in-stay',
      trigger: 'during-stay',
      offsetDays: 1,
      reminderCount: 0,
      reminderSpacingDays: 0,
      isEnabled: false,
    },
    {
      questionnaireType: 'cancellation',
      trigger: 'on-cancel',
      offsetDays: 0,
      reminderCount: 1,
      reminderSpacingDays: 5,
      isEnabled: true,
    },
  ],
  sendWindowFrom: '09:00',
  sendWindowTo: '19:00',
  blackoutDates: ['2026-12-24', '2026-12-25', '2027-01-01'],
  frequencyCapDays: 90,
};

export const GUEST_DATA_DEFAULT: Omit<GuestDataSettings, 'accountId'> = {
  dedupeKey: 'email',
  requiredFields: ['email', 'firstName', 'arrival'],
  excludeStaffRates: true,
  excludeAnonymisedBookings: true,
  excludeOpenComplaints: false,
  minimumStayNights: 1,
};

export const RESPONSE_SETTINGS_DEFAULT: Omit<ResponseSettings, 'accountId'> = {
  responderName: 'Guest Relations Manager',
  signature: 'Warm regards,\nGuest Relations\nSun International',
  requiresApproval: true,
  approverRoleId: null,
  targetResponseHours: 24,
  defaultTone: 'warm',
  autoPublish: false,
};

export const REVIEW_WIDGET_DEFAULT: Omit<ReviewWidgetConfig, 'accountId'> = {
  kind: 'carousel',
  minimumRating: 4,
  sourceKeys: ['survey', 'tripadvisor', 'google'],
  recencyDays: 180,
  useBrandColors: true,
  showGuestName: true,
  maxReviews: 12,
};

// Targets climb with the horizon for a higher-is-better metric and fall for a lower-is-better one:
// a year is long enough to move a number the current month is not.
export const ACCOUNT_GOALS_DEFAULT: Omit<AccountGoals, 'accountId'> = {
  goals: [
    {
      key: 'grr',
      label: 'Guest Review Rating',
      targets: { month: 86, quarter: 88, year: 90 },
      current: 84.2,
      unit: '%',
      higherIsBetter: true,
    },
    {
      key: 'survey-rating',
      label: 'Survey rating',
      targets: { month: 4.4, quarter: 4.5, year: 4.6 },
      current: 4.31,
      unit: '/ 5',
      higherIsBetter: true,
    },
    {
      key: 'online-rating',
      label: 'Online rating',
      targets: { month: 4.3, quarter: 4.4, year: 4.5 },
      current: 4.18,
      unit: '/ 5',
      higherIsBetter: true,
    },
    {
      key: 'nps',
      label: 'Net Promoter Score',
      targets: { month: 50, quarter: 55, year: 62 },
      current: 47,
      unit: '',
      higherIsBetter: true,
    },
    {
      key: 'response-rate',
      label: 'Survey response rate',
      targets: { month: 26, quarter: 30, year: 35 },
      current: 22.6,
      unit: '%',
      higherIsBetter: true,
    },
    {
      key: 'resolution-hours',
      label: 'Average resolution time',
      targets: { month: 28, quarter: 24, year: 18 },
      current: 31,
      unit: 'hrs',
      higherIsBetter: false,
    },
  ],
};

// A new account starts on the same layout a new user gets, with nothing curated yet. `updatedAt`
// stays null until someone here actually sets a default, so the page can say so rather than
// implying a deliberate choice that was never made.
export const ACCOUNT_DASHBOARD_DEFAULTS_DEFAULT: Omit<AccountDashboardDefaults, 'accountId'> = {
  layout: DEFAULT_DASHBOARD_LAYOUT,
  presetIds: [],
  pinnedReportIds: [],
  updatedAt: null,
  lastResetAt: null,
};
